import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import ModalManager from '@ebay/nice-modal-react';

import { CirclePlusBigIcon, MessageIcon } from 'public/icons';

import * as routes from 'pages/routes';

import * as userResource from 'resources/user/hooks';
import * as dealResource from 'resources/deal/hooks';

import * as userHelpers from 'resources/user/user.helpers';

import SampleTransactionModal from 'components/modals/SampleTransactionModal';

import { USER_STATUSES } from 'resources/user/user.constants';
import InviteClientModal from 'modals/InviteClientModal';

export default function useClientCollaborationAction({
  clientId,
  dealId,
  onActionClick,
}) {
  const router = useRouter();

  const { data: client } = userResource.useUserById(clientId);

  const { data: deal } = dealResource.useDealById(dealId);
  const { data: clients } = userResource.useGetUsersByIds(deal?.client_ids);

  const isSampleClient = !!client?.app_state.is_sample;

  const handleResendInvitationClick = useCallback(async () => {
    if (isSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    ModalManager.show(InviteClientModal, {
      clients: [client],
      dealId: deal?.id,
    });

    onActionClick?.();
  }, [isSampleClient, client, deal, onActionClick]);

  const handleInviteAnotherClientClick = useCallback(() => {
    if (isSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    router.push({ pathname: routes.path.manageClients, query: { id: dealId } });
  }, [isSampleClient, router, dealId]);

  const handleInviteExactClientClick = useCallback(() => {
    if (!userHelpers.isNotInvited(client)) {
      return;
    }

    onActionClick?.();

    if (isSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    onActionClick?.();

    ModalManager.show(InviteClientModal, {
      clients: [client],
      dealId: deal.id,
    });
  }, [client, onActionClick, isSampleClient, deal]);

  const handleForceInviteClientsClick = useCallback(() => {
    const notInvitedClients = clients?.filter((clientToInvite) => userHelpers.isNotInvited(clientToInvite));
    if (!notInvitedClients?.length) {
      return;
    }

    if (isSampleClient) {
      ModalManager.show(SampleTransactionModal);
      return;
    }

    onActionClick?.();

    ModalManager.show(InviteClientModal, {
      clients: notInvitedClients,
      dealId: deal.id,
    });
  }, [clients, onActionClick, isSampleClient, deal]);

  const clientStatusToAction = useMemo(() => ({
    [USER_STATUSES.NOT_INVITED]: {
      text: 'Send invitation to collaborate',
      onClick: handleInviteExactClientClick,
      icon: MessageIcon,
    },
    [USER_STATUSES.INVITED]: {
      text: 'Resend invitation to collaborate',
      onClick: handleResendInvitationClick,
      icon: MessageIcon,
    },
    [USER_STATUSES.REGISTERED]: {
      text: 'Add client to transaction',
      onClick: handleInviteAnotherClientClick,
      icon: CirclePlusBigIcon,
    },
  }), [handleInviteExactClientClick, handleResendInvitationClick, handleInviteAnotherClientClick]);

  const clientCollaborationAction = useMemo(
    () => clientStatusToAction[client?.status],
    [clientStatusToAction, client?.status],
  );

  return {
    clientCollaborationAction,
    onInviteClients: handleForceInviteClientsClick,
  };
}
